import { Icon } from "@iconify/react"
import { Button, Card, Drawer, Flex, Form, Grid, Input, message, Pagination, Table } from "antd"
import { useEffect, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useTitleStore from "../../../store/titleStore";
import useDebounce from "../../../hooks/useDebounce";
import { faqCategoryService } from "../../../services/faq-category.service";
import { useTableHeight } from "../../../hooks/useTableHeight";
import TableAction from "../../../components/TableAction";
import { useForm } from "antd/es/form/Form";
import UpdatedByCell from "../../../components/UpdatedByCell";

export default function FaqCategoryIndex(){

  const breakpoint = Grid.useBreakpoint()

  const [ form ] = useForm()
  const { setTitle } = useTitleStore();
  const queryClient = useQueryClient();
  const tableHeight = useTableHeight();

  const [drawerOpt, setDrawerOpt] = useState({
    open: false,
    id: null
  })

  const [messageApi, contextHolder] = message.useMessage();
  const [keyword, setKeyword] = useState("");
  const debouncedKeyword = useDebounce(keyword, 500);
  const [dataParams, setDataParams] = useState({
    limit: 10,
    page: 1,
    filter: []
  })

  const { data: informationData , isLoading, isRefetching } = useQuery({
    queryKey: ['faqCategories', dataParams],
    queryFn: () => faqCategoryService.fetchAll(dataParams),
    placeholderData: keepPreviousData,
  })

  const storeMutation = useMutation({
    mutationFn: data => faqCategoryService.store(data),
    onSuccess: () => {
      handleCloseDrawer()
      queryClient.invalidateQueries('faqCategories');
      messageApi.success('Data berhasil ditambah');
    },
    onError: (error) => {
      console.log(error)
    }
  })

  const updateMutation = useMutation({
    mutationFn: data => faqCategoryService.update(data),
    onSuccess: () => {
      handleCloseDrawer()
      queryClient.invalidateQueries('faqCategories');
      messageApi.success('Data berhasil diubah');
    },
    onError: (error) => {
      console.log(error)
    }
  })

  const { mutate: handleDelete, isPending: deleteLoading } = useMutation({
    mutationFn: (id) => faqCategoryService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries('faqCategories');
      messageApi.success('Data berhasil dihapus');
    },
    onError: (error) => {
      console.log(error)
    }
  })

  const handleSubmit = val => {
    if(drawerOpt.id) updateMutation.mutate({...val, id: drawerOpt.id})
    else storeMutation.mutate(val)
  }

  useEffect(() => {
    setTitle("Kategori FAQ");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDataParams((prev) => ({
      ...prev,
      filter: [
        {
          field: 'name',
          operator: 'like',
          value: debouncedKeyword
        }
      ]
    }));
  }, [debouncedKeyword]);

  const handleChangePage = (page, pageSize) => {
    setDataParams({
      ...dataParams,
      limit: pageSize,
      page
    })
  }

  const handleUpdate = (record) => {
    form.setFieldsValue(record)
    setDrawerOpt({ open: true, id: record.id })
  }

  const handleCloseDrawer = () => {
    setDrawerOpt({ open: false, id: null })
    form.resetFields()
  }

  const columns = [
    {
      title: 'No',
      align: 'center',
      width: 60,
      render: (text, record, index) =>  index + 1,
      fixed: breakpoint.md && 'left'
    },
    {
      title: 'Kategori',
      dataIndex: 'name',
      fixed: breakpoint.md && 'left',
      width: 210,
    },
    {
      title: 'Tanggal Update',
      width: 210,
      render: val => <UpdatedByCell value={val} />,
    },
    {
      className: 'last-cell-p',
      title: 'Aksi',
      align: 'right',
      width: 120,
      fixed: 'right',
      render: record => (
        <TableAction
          onClickEdit={() => handleUpdate(record)}
          handleDelete={() => handleDelete(record.id)}
          deleteLoading={deleteLoading}
        />
      )
    },
  ]

  return (
    <>
      <Card
        styles={{
          body: {
            paddingLeft: 0,
            paddingRight: 0
          }
        }}
      >
        {contextHolder}
        <Flex gap={20} justify="space-between" style={{ paddingInline: 24, marginBottom: 24 }} >
          <Input 
            style={{ width: 320 }} 
            prefix={<Icon icon="hugeicons:search-01" width="16" height="16" />}
            placeholder="cari..."
            onChange={e => setKeyword(e.target.value)}
            value={keyword}
          />
          <Button 
            variant="solid" 
            color="primary"  
            icon={<Icon icon="duo-icons:add-circle" width="22" height="22" />}
            onClick={() => setDrawerOpt({ open: true, id: null })}
            style={{ marginLeft: !breakpoint.md && 'auto', minWidth: 38 }}
          >
            {breakpoint.md && 'Tambah Kategori'}
          </Button>
        </Flex>
        <Table
          columns={columns}
          pagination={false}
          size="small"
          loading={isLoading || isRefetching}
          rowKey={'id'}
          scroll={{ y: tableHeight }}
          dataSource={informationData?.data}
        />
        <Pagination
            style={{ marginInline: 24, marginTop: 16 }}
            total={informationData?.pagination?.total}
            showSizeChanger
            pageSizeOptions={['10', '20', '50']}
            current={informationData?.pagination?.page}
            pageSize={dataParams.limit}
            align="end"
            onChange={handleChangePage}
          />
      </Card>
      <Drawer
        open={drawerOpt.open}
        onClose={handleCloseDrawer}
        title={ (drawerOpt.id ? "Ubah" : "Tambah") + " Data"}
        footer={
          <Flex gap={20} >
            <Button block color="primary" variant="outlined" >
              Batal
            </Button>
            <Button onClick={() => form.submit()} block color="primary" variant="solid" >
              Submit
            </Button>
          </Flex>
        }
      >
        <Form
          layout="vertical"
          onFinish={handleSubmit}
          form={form}
        >
          <Form.Item
            name={'name'}
            label="Kategori"
            rules={[
              {
                required: true
              }
            ]}
          >
            <Input/>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  )
}